/**
 * Typed client for the Drumlin API.
 *
 * Two decisions worth knowing about:
 *
 *  - Every failure is an `ApiError` carrying the server's stable `code` and a
 *    message written for a person. UI code branches on the code and shows the
 *    message; it never composes its own wording for a server-side failure.
 *  - Mutations send an `Idempotency-Key`, so a retry after a dropped connection
 *    cannot create a second organisation, service or database.
 */

/** The error envelope every Drumlin endpoint uses. */
export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  request_id?: string;
}

/** A failed API call. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly requestId: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.details = body.details ?? {};
    this.requestId = body.request_id ?? "";
  }

  /** True when the caller must sign in again. */
  get isUnauthenticated(): boolean {
    return this.code === "unauthenticated";
  }

  /** True when a second factor is outstanding. */
  get needsSecondFactor(): boolean {
    return this.code === "mfa_required";
  }

  /** True when retrying the same request might succeed. */
  get isRetryable(): boolean {
    return (
      this.code === "temporarily_unavailable" ||
      this.code === "rate_limited" ||
      this.status >= 500
    );
  }

  /** Field-level validation problems, keyed by the field they apply to. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    const single = this.details.field;
    if (typeof single === "string") out[single] = this.message;

    const fields = this.details.fields;
    if (Array.isArray(fields)) {
      for (const entry of fields) {
        if (!entry || typeof entry !== "object") continue;
        const { location, message } = entry as { location?: string; message?: string };
        if (!location || !message) continue;
        // "body.display_name" -> "display_name"
        out[location.replace(/^body\./, "")] = message;
      }
    }
    return out;
  }
}

/** Thrown when the network failed before a response arrived. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Could not reach Drumlin. Check your connection and try again.");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

const IDEMPOTENT_METHODS = new Set(["POST", "PATCH", "PUT", "DELETE"]);

/** Generates the idempotency key attached to mutating requests. */
function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Reuse a key across retries of the same logical operation. */
  idempotencyKey?: string;
}

/** Base path. Same origin in production; proxied to the API in development. */
const BASE = "/v1";

function buildURL(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(BASE + path, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.pathname + url.search;
}

/**
 * The absolute path for a streaming endpoint.
 *
 * EventSource takes a URL rather than going through request(), so the base and
 * the query building are shared from here instead of being written a second
 * time at the call site. Same origin, so the session cookie is sent without
 * anything being asked of it.
 */
export function streamURL(path: string, query?: RequestOptions["query"]): string {
  return buildURL(path, query);
}

/** Performs one API call and returns the decoded body. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };

  let body: string | undefined;
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }
  if (IDEMPOTENT_METHODS.has(method)) {
    headers["Idempotency-Key"] = options.idempotencyKey ?? newIdempotencyKey();
  }

  let response: Response;
  try {
    response = await fetch(buildURL(path, options.query), {
      method,
      headers,
      body,
      credentials: "same-origin",
      signal: options.signal ?? null,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new NetworkError(cause);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text ? safeParse(text) : undefined;

  if (!response.ok) {
    const envelope = parsed as { error?: ApiErrorBody } | undefined;
    throw new ApiError(
      response.status,
      envelope?.error ?? {
        code: "internal_error",
        message: "Something went wrong. Please try again.",
      },
    );
  }
  return parsed as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export const api = {
  get: <T>(path: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<T>(path, { method: "GET", query, signal }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "PUT", body }),
  delete: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "DELETE", body }),
};
